import { fromEntries } from '@/helpers/javascript'
import api from '@/api'
import { Module } from 'vuex'
import { EntityList, List, ListEntity, RemoveEntityList, RemoveListEntity, UploadListEntity } from '@/api/lists'

interface Entity {
	id: string
	definitionName: string
	value: any
}

// initial state
const state = () => ({
	lists: {} as Record<string, List>,
	entities: {} as Record<string, Entity>,
	listsEntities: [] as ListEntity[],
	entityLists: {} as Record<string, EntityList>,
})

type ListsState = ReturnType<typeof state>

const store: Module<ListsState, ListsState> = {
	namespaced: true,
	state,
	getters: {},
	actions: {
		async fetchLists({ commit }) {
			const response = await api.lists.get()
			commit('SET_LISTS', response.data)
		},

		async addList ({ commit }, name: string) {
			const response = await api.lists.add(name)
			const data = response.data
			commit('SET_LIST', data)
			return data
		},

		async removeList ({ commit }, name: string) {
			await api.lists.remove(name)
			commit('UNSET_LIST', name)
		},

		async fetchEntityLists ({ commit }, id: string) {
			const response = await api.lists.getLists(id)
			commit('SET_ENTITY_LISTS', response.data)
		},

		async fetchListsEntities ({ commit, dispatch, state }) {
			commit('SET_LIST_ENTITIES', [])
			for (const name of Object.keys(state.lists)) {
				await dispatch('fetchListEntities', name);
			}
		},

		async fetchListEntities ({ commit }, listName: string) {
			const response = await api.lists.getEntities(listName)
			const data = response.data
			data.forEach(e => commit('SET_ENTITY', e))
			commit('ADD_LIST_ENTITIES', data)
		},

		async addToList ({ commit }, { id, listName, reason }: EntityList) {
			const response = await api.lists.addToList({ id, listName, reason })
			commit('SET_ENTITY_LIST', response.data)
		},

		async removeFromList ({ commit }, { id, listName }: RemoveEntityList) {
			await api.lists.removeFromList({ id, listName })
			commit('UNSET_ENTITY_LIST', { listName })
		},

		async addEntity ({ commit }, { id, listName, definitionName, value, reason }: ListEntity) {
			const response = await api.lists.addEntity({ id, listName, definitionName, value, reason })
			const data = response.data
			if (id && data.id !== id) {
				commit('REMOVE_LIST_ENTITY', { id, listName })
			}
			commit('ADD_LIST_ENTITY', data)
		},

		async removeEntity ({ commit }, { id, listName }: RemoveListEntity) {
			await api.lists.removeEntity({ id, listName })
			commit('REMOVE_LIST_ENTITY', { id, listName })
		},

		async upload ({ commit }, { listName, definitionName, reason, files }: UploadListEntity) {
			const response = await api.lists.upload({ listName, definitionName, reason, files })
			commit('ADD_LIST_ENTITIES', response.data)
		},

		setTxEntities ({ commit }, { rows, projection }: { rows: any[], projection: string[] }) {
			rows.forEach(row => {
				projection.forEach((p, i) => {
					if (row[2 * i] !== null) {
						commit('SET_ENTITY', { id: row[2 * i], definitionName: p, value: row[2 * i + 1] })
					}
				})
			})
		}
	},
	mutations: {
		SET_LISTS (state, lists: List[]) {
			state.lists = fromEntries(lists.map(l => [l.name, l]))
		},
		SET_LIST (state, list: List) {
			state.lists[list.name] = list
		},
		UNSET_LIST (state, listName: string) {
			state.listsEntities = state.listsEntities.filter(e => e.listName !== listName)
			delete state.lists[listName]
		},
		SET_ENTITY (state, { id, definitionName, value }: ListEntity) {
			if (!(id in state.entities)) {
				state.entities[id] = { id, definitionName, value }
			}
		},
		SET_ENTITIES (state, entities: Entity[]) {
			state.entities = fromEntries(entities.map(e => [e.id, { id: e.id, definitionName: e.definitionName, value: e.value }]))
		},
		SET_LIST_ENTITIES (state, data: ListEntity[]) {
			state.listsEntities = data
		},
		ADD_LIST_ENTITIES (state, data: ListEntity[]) {
			state.listsEntities = state.listsEntities.concat(data)
		},
		ADD_LIST_ENTITY (state, data: ListEntity) {
			state.listsEntities = state.listsEntities.filter(e => e.id !== data.id || e.listName !== data.listName).concat(data)
		},
		REMOVE_LIST_ENTITY (state, { id, listName }: { id: string, listName: string }) {
			state.listsEntities = state.listsEntities.filter(e => e.id !== id || e.listName !== listName)
		},
		SET_ENTITY_LISTS (state, data: EntityList[]) {
			state.entityLists = fromEntries(data.map(e => [e.listName, e]))
		},
		SET_ENTITY_LIST (state, { listName, reason, added }: EntityList) {
			state.entityLists[listName] = { listName, reason, added }
		},
		UNSET_ENTITY_LIST (state, { listName }: { listName: string }) {
			delete state.entityLists[listName]
		}
	}
}

export default store
